/**
 * Session Context
 *
 * Provides a centralized mechanism for managing and accessing
 * the session chatRoomId across the application.
 *
 * This ensures a single chatRoomId is used consistently throughout a user's session,
 * preventing the issues caused by different components using different chatRoomIds.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getSessionChatRoomId, setSessionChatRoomId } from '../services/session/sessionManager';
import { markSessionChannel } from '../services/chat/chatService';

// Create context
const SessionContext = createContext();

/**
 * SessionProvider Component
 *
 * Wraps the application to provide session state management.
 * Automatically loads any existing session chatRoomId on mount.
 */
export function SessionProvider({ children }) {
  // State to hold the current session chatRoomId
  // Synchronous init: available on first render (no useEffect delay)
  const [sessionChatRoomId, setSessionId] = useState(() => getSessionChatRoomId());

  // State to hold user type: 'anon' | 'profile'
  const [userType, setUserType] = useState(() => {
    return sessionStorage.getItem('user_type') || 'anon';
  });

  // Backwards compatibility: isProfileUser boolean
  const [isProfileUser, setIsProfileUser] = useState(() => {
    return sessionStorage.getItem('user_type') === 'profile';
  });

  // Load session chatRoomId and user type from sessionStorage on mount
  useEffect(() => {
    const storedChatRoomId = getSessionChatRoomId();
    if (storedChatRoomId) {
      setSessionId(storedChatRoomId);
      console.log(`[SESSION_CONTEXT] Loaded existing session chatRoomId: ${storedChatRoomId}`);
    }

    // Load user type from sessionStorage
    // Note: Entry points (QRScreen, AnonExchangeFlow) set the correct user_type
    const storedUserType = sessionStorage.getItem('user_type') || 'anon';
    setUserType(storedUserType);
    setIsProfileUser(storedUserType === 'profile');
    console.log(`[SESSION_CONTEXT] User type loaded: ${storedUserType}`);
  }, []);

  /**
   * Update the session chatRoomId in both context state and sessionStorage
   * For prototype: Always replaces existing sessions by default (force=true)
   *
   * @param {string} chatRoomId - The chat room ID to set
   * @param {boolean} force - If true, override any existing chatRoomId (default: true for prototype)
   * @returns {boolean} True if the chatRoomId was set, false otherwise
   */
  const updateSessionChatRoomId = (chatRoomId, force = true) => {
    if (!chatRoomId) return false;

    // Only update if we don't already have a session ID or if force=true
    if (!sessionChatRoomId || force) {
      markSessionChannel(chatRoomId); // Synchronous — must run before React state update
      setSessionId(chatRoomId);
      setSessionChatRoomId(chatRoomId, force);
      console.log(
        `[SESSION_CONTEXT] ${force ? 'Forced update of' : 'Set new'} session chatRoomId: ${chatRoomId}`
      );
      return true;
    } else {
      console.log(`[SESSION_CONTEXT] Keeping existing session chatRoomId: ${sessionChatRoomId}`);
      return false;
    }
  };

  // Reset the session chatRoomId
  const clearSessionChatRoomId = () => {
    setSessionId(null);
    setSessionChatRoomId(null);
    console.log('[SESSION_CONTEXT] Cleared session chatRoomId');
  };

  // Update user type (primary method)
  const updateUserType = (type) => {
    if (!['anon', 'profile'].includes(type)) {
      console.error(`[SESSION_CONTEXT] Invalid user type: ${type}`);
      return;
    }
    setUserType(type);
    setIsProfileUser(type === 'profile');
    sessionStorage.setItem('user_type', type);
    console.log(`[SESSION_CONTEXT] User type updated to: ${type}`);
  };

  // Update isProfileUser (backwards compatibility - converts to userType)
  const updateIsProfileUser = (isProfile) => {
    if (isProfile) {
      // If setting to profile, use 'profile' type
      updateUserType('profile');
    } else {
      updateUserType('anon');
    }
  };

  // Context value
  const contextValue = {
    sessionChatRoomId,
    updateSessionChatRoomId,
    clearSessionChatRoomId,
    hasActiveSession: !!sessionChatRoomId,
    userType,
    setUserType: updateUserType,
    isProfileUser,
    setIsProfileUser: updateIsProfileUser,
  };

  // Provide context to children
  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
}

/**
 * Custom hook for using the session context
 * @returns {Object} The session context value
 */
export function useSession() {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
}

/**
 * HOC (Higher Order Component) to wrap components that need session access
 * @param {Component} Component - The component to wrap
 * @returns {Component} Wrapped component with session props
 */
export function withSession(Component) {
  return function WrappedComponent(props) {
    const sessionContext = useSession();
    return <Component {...props} session={sessionContext} />;
  };
}
